import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Cog6ToothIcon, PlusIcon, PlayIcon, ChartBarIcon, ClockIcon, TagIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Layout from './Layout';
import TaskAutomationManager from './TaskAutomationManager';
import AutomationStats from './AutomationStats';
import AutomationExecutions from './AutomationExecutions';
import AutomationApprovals from './AutomationApprovals';
import RotationTracker from './RotationTracker';
import PermissionGate from './PermissionGate';

interface Tab {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType;
}

const VALID_AUTOMATION_TABS = ['approvals', 'stats', 'executions', 'rotation-tracker', 'task-automation'];

const Automation: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_AUTOMATION_TABS.includes(tabParam ?? '') ? tabParam! : 'approvals';

  const tabs: Tab[] = [
    {
      id: 'approvals',
      name: 'Pending Approvals',
      icon: ClockIcon,
      component: AutomationApprovals
    },
    {
      id: 'stats',
      name: 'Statistics',
      icon: ChartBarIcon,
      component: AutomationStats
    },
    {
      id: 'executions',
      name: 'Execution History',
      icon: PlayIcon,
      component: AutomationExecutions
    },
    {
      id: 'rotation-tracker',
      name: 'Rotation Tracker',
      icon: ArrowPathIcon,
      component: RotationTracker
    },
    {
      id: 'task-automation',
      name: 'Task Automation',
      icon: Cog6ToothIcon,
      component: TaskAutomationManager
    }
  ];

  const handleTabChange = useCallback((tabId: string) => {
    setSearchParams({ tab: tabId });
  }, [setSearchParams]);

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || TaskAutomationManager;

  return (
    <Layout title="Automation" subtitle="Automate your workflow with intelligent rules and triggers">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Automation</h1>
              <p className="mt-2 text-gray-600">
                Automate your workflow with intelligent rules and triggers
              </p>
            </div>
            <div className="flex items-center space-x-3">
         
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <ActiveComponent />
        </div>
      </div>
    </Layout>
  );
};

export default Automation;
